import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { isOwner as membershipIsOwner } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface SupportGrant {
  id: string;
  tenantId: string;
  operatorUserId: string;
  authorisedByUserId: string;
  reason: string;
  scopes: string[];
  startsAt: string;
  expiresAt: string;
  revokedAt?: string;
}
const scopes = ["tenant_summary", "configuration", "bookings", "billing", "audit"];
const scopeLabels: Record<string, string> = {
  tenant_summary: "Organization summary",
  configuration: "Settings",
  bookings: "Bookings",
  billing: "Billing",
  audit: "Audit history",
};

export function SupportAccessSettings() {
  const auth = useAuth();
  const isOwner = membershipIsOwner(auth.session?.selectedMembership);
  const sensitive = useSensitiveAction({
    setupTitle: "Protect support access with two-step verification",
    lockedTitle: "Confirm your identity to manage support access",
    lockedDescription:
      "Support access can let Pixel Apocalypse staff see selected organization information. Enter a fresh code before viewing or changing it.",
  });
  const grants = useQuery({
    queryKey: ["support-access"],
    queryFn: () => api<ListResponse<SupportGrant>>("/api/v1/support-access"),
    enabled: isOwner && sensitive.unlocked,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [revoking, setRevoking] = useState<SupportGrant>();

  async function authorise(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/support-access", {
        method: "POST",
        body: jsonBody({
          operatorUserId: String(form.get("operatorUserId")),
          reason: String(form.get("reason")),
          scopes: form.getAll("scopes").map(String),
          expiresAt: new Date(String(form.get("expiresAt"))).toISOString(),
        }),
      });
      formElement.reset();
      await grants.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function revoke(reason: string) {
    if (!revoking) return;
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/support-access/${revoking.id}`, {
        method: "DELETE",
        body: jsonBody({ reason }),
      });
      setRevoking(undefined);
      await grants.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (!isOwner)
    return (
      <Notice tone="warning">
        Only your organization owner can give Pixel Apocalypse support access.
      </Notice>
    );
  if (!sensitive.unlocked) return <div className="stack">{sensitive.view}</div>;
  if (grants.isLoading) return <PanelSkeleton />;
  if (grants.error) return <ErrorPanel error={grants.error} />;
  const items = grants.data?.items ?? [];
  return (
    <div className="stack">
      <Notice tone="warning" title="You stay in control">
        Pixel Apocalypse staff cannot see your organization by default. You choose exactly what they
        may view for up to 24 hours, and you can remove access at any time. A fresh two-step
        verification code is required.
      </Notice>
      {sensitive.view}
      {error ? <ErrorPanel error={error} /> : null}
      <Card>
        <CardHeader
          title="Give temporary support access"
          description="Pixel Apocalypse support will give you the staff ID and tell you which information is needed for your case."
        />
        <form className="form-grid" onSubmit={authorise}>
          <Field label="Support staff ID">
            <input
              name="operatorUserId"
              required
              placeholder="ID supplied by Pixel Apocalypse support"
            />
          </Field>
          <Field label="Access ends">
            <input name="expiresAt" type="datetime-local" required />
          </Field>
          <Field label="Why access is needed">
            <textarea name="reason" required minLength={10} />
          </Field>
          <fieldset>
            <legend>Information support may view</legend>
            <div className="form-grid">
              {scopes.map((scope) => (
                <label className="check-field" key={scope}>
                  <input
                    name="scopes"
                    value={scope}
                    type="checkbox"
                    defaultChecked={scope === "tenant_summary"}
                  />{" "}
                  {scopeLabels[scope] ?? titleCase(scope)}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="full">
            <FormActions
              submitting={working}
              disabled={!sensitive.unlocked}
              submitLabel="Give temporary access"
            />
          </div>
        </form>
      </Card>
      <Card>
        <CardHeader title="Current support access" />
        {items.length ? (
          <DataTable
            caption="Current support access"
            items={items}
            getKey={(item) => item.id}
            columns={[
              {
                label: "Support staff member",
                render: (item) => <span className="mono">{item.operatorUserId}</span>,
              },
              {
                label: "Information",
                render: (item) =>
                  item.scopes.map((scope) => scopeLabels[scope] ?? titleCase(scope)).join(", "),
              },
              { label: "Access ends", render: (item) => formatDate(item.expiresAt) },
              {
                label: "Status",
                render: (item) => (
                  <StatusPill
                    value={
                      item.revokedAt
                        ? "revoked"
                        : new Date(item.expiresAt) <= new Date()
                          ? "expired"
                          : "active"
                    }
                  />
                ),
              },
              {
                label: "Action",
                render: (item) =>
                  item.revokedAt || new Date(item.expiresAt) <= new Date() ? (
                    "Not available"
                  ) : (
                    <button
                      className="button danger small"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={() => setRevoking(item)}
                    >
                      Remove access
                    </button>
                  ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No support access"
            description="No Pixel Apocalypse staff can currently view your organization."
          />
        )}
      </Card>
      <ReasonDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(undefined)}
        onSubmit={revoke}
        title="Remove support access"
        description={
          revoking
            ? `Support staff member ${revoking.operatorUserId} will immediately lose access to this organization.`
            : undefined
        }
        initialReason="The approved support activity has ended."
        submitLabel="Remove access"
        tone="danger"
        minLength={10}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </div>
  );
}
